# Unposted positions

Unposted positions allow your software to share information about positions that are currently being hired for.
All positions being actively recruited for should be shared with Talent Search.
When a hirer [purchases a profile] in Talent Search they will be prompted to select an unposted position to associate the purchase with.

Changes to positions within your software should be reflected in the SEEK API [within 60 seconds].

[within 60 seconds]: https://au.employer.seek.com/partners/terms-of-use#talent-search-connect

## Position openings

[`PositionOpening`s] are the SEEK API’s representation of a job requisition.
A given opening may be shared between Job Posting and Talent Search Connect use cases.

See the [position opening documentation] for further context and supported operations.

## Unposted position profiles

An [`UnpostedPositionProfile`] represents the details of an open position not posted to the SEEK job board.
They share most of their structure with the [`PostedPositionProfile`] used to represent a posted job ad.
However, they lack the [`PostingInstruction`] object used to facilitate the posting process.

### Create an unposted position profile

The [`createUnpostedPositionProfileForOpening` mutation] creates an unposted position profile under an existing position opening.

```graphql
mutation ($input: CreateUnpostedPositionProfileForOpeningInput!) {
  createUnpostedPositionProfileForOpening(input: $input) {
    positionProfile {
      profileId {
        value
      }
    }
  }
}
```

```json
{
  "input": {
    "positionProfile": {
      "jobCategories": [],
      "offeredRemunerationPackage": {
        "basisCode": "Salaried",
        "ranges": {
          "minimumAmount": {
            "value": 100000,
            "currency": "AUD"
          },
          "maximumAmount": {
            "value": 130000,
            "currency": "AUD"
          },
          "intervalCode": "Year"
        },
        "descriptions": ["Up to $130k excluding super"]
      },
      "positionFormattedDescriptions": [],
      "positionLocation": [],
      "positionOpeningId": "globalPublicTest:positionOpening:positions:F65aT5rAv3ZSusuunFWT8i",
      "positionOrganizations": "seekAnzPublicTest:organization:seek:93WyyF1h",
      "positionTitle": "Customer Mobility Director",
      "profileName": "Acme Corp"
    }
  }
}
```

```json
{
  "createUnpostedPositionProfileForOpening": {
    "positionProfile": {
      "profileId": {
        "value": "globalPublicTest:positionProfile:unposted:A2qKvDuQ1ydkumY56qBP38"
      }
    }
  }
}
```

`profileName` is an optional free-text input field that is rendered alongside the `positionTitle` to help disambiguate the profile.
This is useful for agencies that may hire similar positions on behalf of multiple hirers.
For example, if you supply the name of the position’s company, the end user will see a Talent Search combobox along the lines of:

import { PositionSelector } from './PositionSelector';

<PositionSelector />

You can hover your mouse over the above example to view the corresponding input fields.

### Update an unposted position profile

The [`updateUnpostedPositionProfile` mutation] updates an existing unposted position profile.

The position profile’s existing fields will be replaced with the provided input fields.
You must provide the current values of any fields you don’t want to modify.

```graphql
mutation ($input: UpdateUnpostedPositionProfileInput!) {
  updateUnpostedPositionProfile(input: $input) {
    positionProfile {
      profileId {
        value
      }
    }
  }
}
```

```json
{
  "input": {
    "positionProfile": {
      "jobCategories": [],
      "offeredRemunerationPackage": {
        "basisCode": "Salaried",
        "ranges": {
          "minimumAmount": {
            "value": 100000,
            "currency": "AUD"
          },
          "maximumAmount": {
            "value": 130000,
            "currency": "AUD"
          },
          "intervalCode": "Year"
        },
        "descriptions": ["Up to $130k excluding super"]
      },
      "positionFormattedDescriptions": [],
      "positionLocation": [],
      "positionTitle": "VP of Customer Mobility",
      "profileId": "globalPublicTest:positionProfile:unposted:GSg8dZtux2FD8hP4Bmju3B"
    }
  }
}
```

```json
{
  "updateUnpostedPositionProfile": {
    "positionProfile": {
      "profileId": {
        "value": "globalPublicTest:positionProfile:unposted:GSg8dZtux2FD8hP4Bmju3B"
      }
    }
  }
}
```

### Delete an unposted position profile

The [`deleteUnpostedPositionProfile` mutation] deletes an unposted position profile and removes the unposted position from Talent Search.
Your software should use this mutation when the corresponding position is no longer being hired for.

This will not remove information that has been associated with the position profile,
such as [placement information] within a candidate’s interaction history.
This operation should be avoided once other objects reference the position profile.

```graphql
mutation ($input: DeleteUnpostedPositionProfileInput!) {
  deleteUnpostedPositionProfile(input: $input) {
    positionProfile {
      profileId {
        value
      }
    }
  }
}
```

```json
{
  "input": {
    "positionProfile": {
      "profileId": "globalPublicTest:positionProfile:unposted:GSg8dZtux2FD8hP4Bmju3B"
    }
  }
}
```

```json
{
  "deleteUnpostedPositionProfile": {
    "positionProfile": {
      "profileId": {
        "value": "globalPublicTest:positionProfile:unposted:GSg8dZtux2FD8hP4Bmju3B"
      }
    }
  }
}
```

[`createunpostedpositionprofileforopening` mutation]: /schema/mutation/createUnpostedPositionProfileForOpening
[`deleteunpostedpositionprofile` mutation]: /schema/mutation/deleteUnpostedPositionProfile
[`positionopening`s]: /schema/named-type/PositionOpening
[`postedpositionprofile`]: /schema/named-type/PostedPositionProfile
[`postinginstruction`]: /schema/named-type/PostingInstruction
[`unpostedpositionprofile`]: /schema/named-type/UnpostedPositionProfile
[`updateunpostedpositionprofile` mutation]: /schema/mutation/updateUnpostedPositionProfile
[placement information]: /use-cases/proactive-sourcing/interaction-history#placement-information
[position opening documentation]: /use-cases/job-posting/position-openings
[purchases a profile]: /use-cases/proactive-sourcing/profile-purchases
